// VisitBlock (round 2): one visit on the board, 56 px tall, three lines that never wrap (DESIGN.md §8):
// the time window, strong; who it's for; its state and job number. Its drawing says how it stands
// (§2), so the words never repeat it. A visit with a pen has a 4 px stripe of it down its leading edge.
// Booked is filled with a wash of the technician's pen (option D, 26 Oct), working now is filled with
// the pen itself, done is hatched in it with muted words, paused is dashed, cancelled (the job page's
// Visits) is a muted outline with its window struck, and a problem is red on the plain sheet (no one on
// it, late, at risk). Sent and On the way are drawn as booked, told apart by their words: On the way
// only once the technician has tapped it (BL-158 8). Running over keeps its fill with a red ring; the
// lane draws the over-run. Selected has the heavier ink line. While a drag is on, the visit that's lifted
// is dashed and muted, and its ghost, where it would land, is dashed in the pen: solid when it fits, red
// when it can't. Compact is one line, time and who for. Too narrow, it gives up words from the bottom,
// then the window for the start, then everything: the hover card and the label still say it all. A
// late or unassigned visit too narrow for its name and its words keeps the name, on two lines, and its
// problem is the red drawing alone. The visit its technician is on now carries a Now tag, filled; the
// one queued to go out next for them a Next tag, outlined (BL-070); work that needs a licence nobody on
// it holds a Licence tag, red, on the third line (BL-027). Compact and narrow blocks give the tags up
// (the label still says them). A short block never draws blank (QA-763): narrower than its start, it
// keeps the customer's name, cut short; the label and the hover card say it in full. A visit whose day
// went by while it was still booked or sent is Not attended (QA-764): drawn, not coloured (DESIGN.md
// §2), the booking's outline gone muted on the plain sheet, its words in ink, saying Not attended.
// There is no native title: the HoverCard is its only hover (BL-158 9). Reads only --visit-*.
import type { ButtonHTMLAttributes, Ref } from 'react';
import type { Density } from '../time-span.tsx';
import { penStyle, type Pen } from '../pen.ts';
import './VisitBlock.css';

export const VISIT_STATES = [
  'booked',
  'sent',
  'on-the-way',
  'working',
  'paused',
  'done',
  'unassigned',
  'cancelled',
  'missed',
] as const;
export type VisitState = (typeof VISIT_STATES)[number];

/** The words a state puts on the block's third line; the drawing says booked, working and done. */
export const STATE_WORDS: Record<VisitState, string | null> = {
  booked: null,
  sent: 'Sent',
  'on-the-way': 'On the way',
  working: null,
  paused: 'Paused',
  done: null,
  unassigned: 'No one yet',
  cancelled: 'Cancelled',
  missed: 'Not attended',
};

export type VisitDrag = 'lifted' | 'ghost' | 'ghost-bad';
export type VisitTag = 'now' | 'next';

/** A tag's words: the label says them too (", now", ", next"), so the tag is hidden from screen readers. */
export const TAG_WORDS: Record<VisitTag, string> = { now: 'Now', next: 'Next' };

export interface VisitBlockProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'title' | 'aria-label'
> {
  state: VisitState;
  /** The lead technician's pen; none for nobody. */
  pen?: Pen | null;
  /** Its own times: "10–11 a.m." (QA-762: the visit's start and end, not its arrival window). */
  time: string;
  /** Its start alone, for a block too narrow for the window: "10", "1:30". */
  timeShort: string;
  /** Who it's for: "Hollis residence". */
  customer: string;
  /** The job's number: "#24611". */
  job: string;
  /** A problem, in words, red: "Late 25 min", "20 min over", "May start late". */
  late?: string | null;
  /** The visit its technician is on now ('now'), or the one queued to go out next for them ('next'). */
  tag?: VisitTag | null;
  /** The work needs a licence nobody on it holds (BL-027): a red Licence tag. */
  licence?: boolean;
  selected?: boolean;
  drag?: VisitDrag;
  density?: Density;
  /** A short visit (15 or 20 min): the padding gives way so its start still shows. */
  narrow?: boolean;
  /** Everything about it in one sentence, for screen readers. */
  label: string;
  ref?: Ref<HTMLButtonElement>;
}

/** A visit reads its pen's line and its wash. */
const VISIT_PEN_PARTS = ['pen', 'wash'] as const;

/** Hyphens that don't break: on two narrow lines "Fairfield Co-op" breaks at its space, not "Co-|op". */
const keepHyphens = (words: string) => words.replace(/-/g, '\u2011');

export function VisitBlock({
  state,
  pen,
  time,
  timeShort,
  customer,
  job,
  late,
  tag,
  licence,
  selected,
  drag,
  density = 'comfortable',
  narrow = false,
  label,
  ref,
  style,
  ...button
}: VisitBlockProps) {
  const word = late ?? STATE_WORDS[state];
  const drawnPen = state === 'unassigned' || state === 'cancelled' ? null : (pen ?? null);
  return (
    <button
      type="button"
      {...button}
      ref={ref}
      className="fl-visit"
      data-state={state}
      data-late={late ? '' : undefined}
      data-selected={selected ? '' : undefined}
      data-drag={drag}
      data-density={density}
      data-narrow={narrow ? '' : undefined}
      data-pen={drawnPen ?? undefined}
      aria-label={label}
      aria-pressed={selected ?? undefined}
      style={{ ...penStyle('visit', drawnPen, VISIT_PEN_PARTS), ...style }}
    >
      <span className="fl-visit__body">
        {density === 'compact' ? (
          <span className="fl-visit__line" data-line="one">
            <span className="fl-visit__time" data-long="">
              {time}
            </span>
            <span className="fl-visit__time" data-short="">
              {timeShort}
            </span>
            <span className="fl-visit__who">{customer}</span>
          </span>
        ) : (
          <>
            <span className="fl-visit__line fl-visit__time" data-long="">
              {tag ? (
                <span className="fl-visit__tag" data-tag={tag} aria-hidden="true">
                  {TAG_WORDS[tag]}
                </span>
              ) : null}
              {time}
            </span>
            <span className="fl-visit__line fl-visit__time" data-short="">
              {timeShort}
            </span>
            <span className="fl-visit__line fl-visit__who">{keepHyphens(customer)}</span>
            <span className="fl-visit__line fl-visit__state">
              {licence ? (
                <span className="fl-visit__tag" data-tag="licence" aria-hidden="true">
                  Licence
                </span>
              ) : null}
              {word ? `${word}, ${job}` : job}
            </span>
          </>
        )}
      </span>
    </button>
  );
}
